import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { cn } from "@/shared/lib/utils";
import { SeeMoreSection } from "../SeeMoreSection";
import { useStoreNavigation, useFilterTableScope } from "../storeNavigation";
import type { FilterTableColumn, FilterTableFlag, FilterTableRow } from "../types";

interface FilterTableProps {
  title: string;
  columns: FilterTableColumn[];
  rows: FilterTableRow[];
  flags?: FilterTableFlag[];
  scope?: { label: string; options: string[]; mode: "server" | "client" };
  head?: number;
  noun?: string;
  defaultSort?: { key: string; dir: "asc" | "desc" };
  emptyState?: string;
}

interface SortState {
  key: string;
  dir: "asc" | "desc";
}

/** Sentinel for the scope select's "no scope" entry — Radix Select forbids
 *  empty-string item values. */
const SCOPE_ALL = "__all__";

/** Sort key for a cell: the server-shipped sort value when present, else the
 *  display value itself. */
function sortValueOf(row: FilterTableRow, colIdx: number): string | number | null {
  return row.sort_values?.[colIdx] ?? row.cells[colIdx] ?? null;
}

function compareValues(a: string | number | null, b: string | number | null, dir: "asc" | "desc") {
  if (a == null && b == null) return 0;
  if (a == null) return 1; // nulls always last
  if (b == null) return -1;
  const cmp =
    typeof a === "string" || typeof b === "string"
      ? String(a).localeCompare(String(b))
      : a - b;
  return dir === "asc" ? cmp : -cmp;
}

/** Membership map for the 4 ntile buckets over a worse-first (descending)
 *  ordering of one column's sort keys — faithful Postgres ntile(4): the first
 *  n%4 buckets take the extra row, nulls rank last (never in the worst
 *  bucket). Returns row → bucket index (0 = worst). */
function ntile4(rows: FilterTableRow[], colIdx: number): Map<FilterTableRow, number> {
  const ranked = [...rows].sort((a, b) => {
    const av = sortValueOf(a, colIdx);
    const bv = sortValueOf(b, colIdx);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av === bv) return 0;
    // Higher = worse → descending.
    return typeof av === "string" || typeof bv === "string"
      ? String(bv).localeCompare(String(av))
      : bv - av;
  });
  const n = ranked.length;
  const baseSize = Math.floor(n / 4);
  const extras = n % 4;
  const out = new Map<FilterTableRow, number>();
  let idx = 0;
  for (let bucket = 0; bucket < 4; bucket++) {
    const size = baseSize + (bucket < extras ? 1 : 0);
    for (let k = 0; k < size && idx < n; k++, idx++) out.set(ranked[idx], bucket);
  }
  return out;
}

/**
 * Declarative sortable / flag-filterable / scopeable table (the `filter_table`
 * kind). Sort, flag chips and quartile tones ALWAYS operate on the full
 * scoped set (no silent top-N); the see-more head only limits what is
 * visible. Built on the same CSS-grid role="table" idiom as SeeMoreSection's
 * other consumers so overflow rows animate without breaking column alignment.
 */
export function FilterTable({
  title,
  columns,
  rows,
  flags,
  scope,
  head,
  noun,
  defaultSort,
  emptyState,
}: FilterTableProps) {
  const nav = useStoreNavigation();
  const serverScope = useFilterTableScope();
  const [activeFlags, setActiveFlags] = useState<ReadonlySet<string>>(new Set());
  const [clientScope, setClientScope] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState | null>(defaultSort ?? null);

  const isServerScope = scope?.mode === "server";
  const selectedScope = isServerScope ? (serverScope?.selected ?? null) : clientScope;

  // 1. Scoped universe. Server mode ships pre-scoped rows; client mode
  //    filters by scope_value locally.
  const universe = useMemo(() => {
    if (!scope || isServerScope || selectedScope == null) return rows;
    return rows.filter((r) => r.scope_value === selectedScope);
  }, [rows, scope, isServerScope, selectedScope]);

  // 2. Quartile buckets per toned column, over the CURRENT scoped universe.
  const quartiles = useMemo(() => {
    const out = new Map<number, Map<FilterTableRow, number>>();
    columns.forEach((col, ci) => {
      if (col.quartile_tone) out.set(ci, ntile4(universe, ci));
    });
    return out;
  }, [columns, universe]);

  // 3. Live chip counts (over the scoped universe) + AND filter.
  const chipCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const f of flags ?? []) {
      counts.set(f.key, universe.filter((r) => r.flags?.includes(f.key)).length);
    }
    return counts;
  }, [flags, universe]);

  const filtered = useMemo(
    () => universe.filter((r) => [...activeFlags].every((f) => r.flags?.includes(f))),
    [universe, activeFlags],
  );

  // 4. Sort — always over the full filtered set; nulls last, ties broken by
  //    store_number then original (server) order. No sort state = server order.
  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const colIdx = columns.findIndex((c) => c.key === sort.key);
    if (colIdx < 0) return filtered;
    return filtered
      .map((row, i) => ({ row, i }))
      .sort((a, b) => {
        const cmp = compareValues(sortValueOf(a.row, colIdx), sortValueOf(b.row, colIdx), sort.dir);
        if (cmp !== 0) return cmp;
        const tie = (a.row.store_number ?? "").localeCompare(b.row.store_number ?? "");
        if (tie !== 0) return tie;
        return a.i - b.i;
      })
      .map((e) => e.row);
  }, [filtered, sort, columns]);

  const toggleFlag = (key: string) =>
    setActiveFlags((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const onHeaderClick = (col: FilterTableColumn) =>
    setSort((prev) =>
      prev?.key === col.key
        ? { key: col.key, dir: prev.dir === "desc" ? "asc" : "desc" }
        : // Text columns read best ascending; numeric (right-aligned) worst-first.
          { key: col.key, dir: col.align === "right" ? "desc" : "asc" },
    );

  const onScopeChange = (v: string) => {
    const next = v === SCOPE_ALL ? null : v;
    if (isServerScope) serverScope?.setScope(next);
    else setClientScope(next);
  };

  if (rows.length === 0 && emptyState) {
    return <p className="px-5 py-4 text-sm text-muted-foreground">{emptyState}</p>;
  }

  const gridTemplate = columns.map((c) => c.grid ?? "minmax(0, 1fr)").join(" ");
  const filtersActive = activeFlags.size > 0 || selectedScope != null;

  const renderRow = (row: FilterTableRow, ri: number) => {
    const storeNumber = row.store_number;
    const openStore =
      nav && storeNumber ? () => nav.openStore(storeNumber, String(row.cells[0] ?? "")) : null;
    return (
      <div
        key={ri}
        role="row"
        className="grid items-center border-b border-border transition-colors last:border-0 hover:bg-muted/50"
        style={{ gridTemplateColumns: gridTemplate }}
      >
        {row.cells.map((cell, ci) => {
          const col = columns[ci];
          const bucket =
            col?.quartile_tone && sortValueOf(row, ci) != null
              ? quartiles.get(ci)?.get(row)
              : undefined;
          const tone =
            bucket === 0
              ? "text-status-critical"
              : bucket === 1
                ? "text-status-warning"
                : undefined;
          return (
            <div
              key={ci}
              role="cell"
              className={cn(
                "px-4 py-2.5 text-sm tabular-nums truncate",
                col?.align === "right" ? "text-right" : "text-left",
                tone,
              )}
            >
              {ci === 0 && openStore ? (
                <button
                  type="button"
                  onClick={openStore}
                  className="cursor-pointer truncate text-left underline-offset-2 hover:underline focus-visible:outline-none focus-visible:underline"
                >
                  {cell ?? "—"}
                </button>
              ) : (
                (cell ?? "—")
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      {flags?.length || scope ? (
        <div className="flex flex-wrap items-center gap-2 px-5 pb-3 pt-2">
          {(flags ?? []).map((f) => {
            const pressed = activeFlags.has(f.key);
            return (
              <button
                key={f.key}
                type="button"
                aria-pressed={pressed}
                onClick={() => toggleFlag(f.key)}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium tracking-wide",
                  "cursor-pointer select-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                  pressed
                    ? "border-status-warning/60 bg-status-warning/20 text-status-warning"
                    : "border-border bg-transparent text-muted-foreground hover:bg-accent/40",
                )}
              >
                {f.label}
                <span className="tabular-nums opacity-80">({chipCounts.get(f.key) ?? 0})</span>
              </button>
            );
          })}
          {scope ? (
            <div className="ml-auto">
              <Select
                value={selectedScope ?? SCOPE_ALL}
                onValueChange={onScopeChange}
                // Server-mode scope needs a surface to own the refetch; with no
                // provider the select stays visible but inert.
                disabled={isServerScope && !serverScope}
              >
                <SelectTrigger className="h-8 w-56 text-xs" aria-label={scope.label}>
                  <SelectValue placeholder={scope.label} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SCOPE_ALL} className="text-xs">
                    {scope.label}
                  </SelectItem>
                  {scope.options.map((o) => (
                    <SelectItem key={o} value={o} className="text-xs">
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>
      ) : null}

      {isServerScope && serverScope?.loading ? (
        <p className="px-5 pb-2 text-xs text-muted-foreground">Loading scoped view…</p>
      ) : null}
      {isServerScope && serverScope?.error ? (
        <p className="px-5 pb-2 text-xs text-status-warning">{serverScope.error}</p>
      ) : null}

      <div
        role="table"
        aria-label={title}
        className={cn(
          "w-full overflow-x-auto border-t border-border",
          isServerScope && serverScope?.loading && "opacity-60 pointer-events-none",
        )}
      >
        <div
          role="row"
          className="grid border-b border-border"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          {columns.map((col) => {
            const active = sort?.key === col.key;
            return (
              <button
                key={col.key}
                type="button"
                role="columnheader"
                aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                onClick={() => onHeaderClick(col)}
                className={cn(
                  "flex items-center gap-1 px-4 py-3 text-xs font-medium",
                  col.align === "right" ? "justify-end text-right" : "justify-start text-left",
                  "cursor-pointer hover:text-foreground focus-visible:outline-none focus-visible:text-foreground",
                  active ? "text-foreground" : "text-muted-foreground",
                )}
              >
                <span className="truncate">{col.label}</span>
                {active ? (
                  sort.dir === "desc" ? (
                    <ArrowDown className="h-3 w-3 shrink-0" />
                  ) : (
                    <ArrowUp className="h-3 w-3 shrink-0" />
                  )
                ) : null}
              </button>
            );
          })}
        </div>
        {sorted.length === 0 && filtersActive ? (
          <p className="px-5 py-4 text-sm text-muted-foreground">
            No rows match the selected filters.
          </p>
        ) : null}
        <SeeMoreSection
          head={head ?? 10}
          total={rows.length}
          matching={sorted.length}
          noun={noun ?? "rows"}
        >
          {({ start, end }) => <div>{sorted.slice(start, end).map(renderRow)}</div>}
        </SeeMoreSection>
      </div>
    </div>
  );
}
